import React from 'react';
import { Star } from 'lucide-react';

interface StarRatingProps {
  rating: number;
  maxStars?: number;
  size?: 'sm' | 'md' | 'lg';
  showNumber?: boolean;
  className?: string;
}

const StarRating: React.FC<StarRatingProps> = ({
  rating,
  maxStars = 5,
  size = 'md',
  showNumber = true,
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  const clampedRating = Math.max(0, Math.min(rating, maxStars));
  const fullStars = Math.floor(clampedRating);
  const fractionalPart = clampedRating - fullStars; // 0.0 - 1.0
  const hasFractionalStar = fractionalPart > 0.001;
  const emptyStars = maxStars - fullStars - (hasFractionalStar ? 1 : 0);

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <div className="flex">
        {/* Full stars */}
        {Array.from({ length: fullStars }).map((_, i) => (
          <Star
            key={`full-${i}`}
            className={`${sizeClasses[size]} text-yellow-400 fill-yellow-400`}
          />
        ))}
        
        {/* Fractional star with precise percentage fill */}
        {hasFractionalStar && (
          <div className="relative">
            <Star className={`${sizeClasses[size]} text-yellow-400`} />
            <Star 
              className={`${sizeClasses[size]} text-yellow-400 fill-yellow-400 absolute top-0 left-0`} 
              style={{ 
                clipPath: `inset(0 ${Math.max(0, 100 - Math.round(fractionalPart * 100))}% 0 0)`
              }}
            />
          </div>
        )}
        
        {/* Empty stars */}
        {Array.from({ length: emptyStars }).map((_, i) => (
          <Star
            key={`empty-${i}`}
            className={`${sizeClasses[size]} text-gray-400`}
          />
        ))}
      </div>
      
      {showNumber && (
        <span className={`${textSizeClasses[size]} text-gray-300 font-medium`}>
          {clampedRating.toFixed(2)}
        </span>
      )}
    </div>
  );
};

export default StarRating;
